import React from 'react';

// Individual skeleton components
const SkeletonLine = ({ width = "100%", height = "h-4", className = "" }) => (
  <div
    className={`bg-gray-200 rounded animate-pulse ${height} ${className}`}
    style={{ width }}
  />
);

const SkeletonCard = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 animate-pulse">
    {/* Header section */}
    <div className="flex items-start justify-between">
      <div className="space-y-2 flex-1">
        <SkeletonLine width="200px" height="h-6" />
        <SkeletonLine width="150px" height="h-4" />
      </div>
      <div className="w-20 h-8 bg-gray-200 rounded-full" />
    </div>

    {/* Content lines */}
    <div className="space-y-2">
      <SkeletonLine width="60%" height="h-3" />
      <SkeletonLine width="40%" height="h-3" />
    </div>

    {/* Bottom section */}
    <div className="flex justify-between items-center pt-2">
      <div className="flex items-center space-x-2">
        <div className="w-2 h-2 bg-gray-200 rounded-full" />
        <SkeletonLine width="120px" height="h-3" />
      </div>
      <div className="w-16 h-6 bg-gray-200 rounded" />
    </div>
  </div>
);

const SkeletonForm = () => (
  <div className="space-y-6">
    {/* Form sections */}
    {[1, 2, 3].map((section) => (
      <div key={section} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 animate-pulse">
        <SkeletonLine width="200px" height="h-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <SkeletonLine width="80px" height="h-4" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
          <div className="space-y-2">
            <SkeletonLine width="100px" height="h-4" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
          <div className="space-y-2">
            <SkeletonLine width="90px" height="h-4" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
        </div>
        <div className="space-y-2">
          <SkeletonLine width="70%" height="h-3" />
          <SkeletonLine width="50%" height="h-3" />
        </div>
      </div>
    ))}
  </div>
);

const SkeletonJobsList = () => (
  <div className="space-y-4">
    {/* Header skeleton */}
    <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <SkeletonLine width="150px" height="h-8" />
          <SkeletonLine width="300px" height="h-4" />
        </div>
        <div className="w-32 h-10 bg-gray-200 rounded-lg" />
      </div>
    </div>

    {/* Job cards skeleton */}
    {[1, 2, 3, 4].map((item) => (
      <SkeletonCard key={item} />
    ))}
  </div>
);

const SkeletonJobDetail = () => (
  <div className="flex flex-col lg:flex-row gap-6">
    {/* Main content */}
    <div className="flex-1">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center animate-pulse">
        <SkeletonLine width="250px" height="h-8" />
        <div className="flex gap-3">
          <div className="w-16 h-8 bg-gray-200 rounded" />
          <div className="w-16 h-8 bg-gray-200 rounded" />
        </div>
      </div>

      <SkeletonForm />
    </div>

    {/* Sidebar */}
    <div className="hidden lg:block lg:w-80 lg:flex-shrink-0">
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 animate-pulse">
        <SkeletonLine width="80px" height="h-6" />
        <div className="space-y-3">
          <SkeletonLine width="100%" height="h-4" />
          <SkeletonLine width="80%" height="h-4" />
          <SkeletonLine width="60%" height="h-4" />
        </div>
        <div className="pt-4 border-t border-gray-100">
          <SkeletonLine width="70%" height="h-4" />
        </div>
      </div>
    </div>
  </div>
);

// Main skeleton loader component
const SkeletonLoader = ({ type = "default" }) => {
  const skeletonTypes = {
    jobsList: <SkeletonJobsList />,
    jobDetail: <SkeletonJobDetail />,
    form: <SkeletonForm />,
    default: (
      <div className="space-y-4">
        {[1, 2, 3].map((item) => (
          <SkeletonCard key={item} />
        ))}
      </div>
    )
  };

  return (
    <div className="animate-pulse">
      {skeletonTypes[type] || skeletonTypes.default}
    </div>
  );
};

export default SkeletonLoader;
